import type { User } from '@shared/schemas';
import { supabase } from '../supabaseClient';
import { assembleUser, minimalUser } from './me';

/**
 * Auth endpoints — Supabase Auth replaces the 11 Hono /api/auth/* routes
 * (ADR-012 · slot slot_supabase_migration · SM.3). Every flow is a client-side
 * supabase-js call; the hand-rolled auth server (Argon2id / JWT / cookies) is
 * retired.
 *
 * Convention: methods resolve on success and throw an `Error` carrying a
 * user-facing RU message on failure — `AuthContext` already wraps these calls
 * in try/catch, so the contract its screens see is unchanged.
 *
 * 11-endpoint mapping (parity ledger · SM.9):
 *   signup · login · logout · forgot-password · reset-password · google/start
 *   are the methods below. refresh → supabase-js auto-refresh. verify-email,
 *   google/callback, set-password, me → handled in supabaseClient / me.ts /
 *   the SM.3b profile screen. updateProfile / updatePassword (SM.3b) cover the
 *   editable-profile account section.
 */

export interface SignupBody {
  email: string;
  password: string;
  displayName?: string;
}

export interface LoginBody {
  email: string;
  password: string;
}

export interface ResetPasswordBody {
  /** Retained for call-site compatibility. Supabase carries the recovery
   *  session in the URL (detectSessionInUrl), so the token value is unused. */
  token?: string;
  password: string;
}

export interface ForgotBody {
  email: string;
}

function appOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

export const authEndpoints = {
  /**
   * Email/password signup · Supabase sends the confirmation email. The result
   * is account-enumeration-safe: signUp against an already-registered address
   * resolves without revealing it, so the caller always reaches the uniform
   * "pending verification" state (parity with the old 202 response).
   */
  async signup(body: SignupBody): Promise<{ status: string }> {
    const { error } = await supabase.auth.signUp({
      email: body.email,
      password: body.password,
      options: {
        emailRedirectTo: `${appOrigin()}/verify`,
        data: body.displayName ? { display_name: body.displayName } : {},
      },
    });
    if (error) throw new Error(mapAuthError(error.message));
    return { status: 'pending_verification' };
  },

  /** Email/password login · throws on bad credentials or unconfirmed email. */
  async login(body: LoginBody): Promise<{ user: User }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: body.email,
      password: body.password,
    });
    if (error || !data.user) throw new Error(mapAuthError(error?.message ?? ''));
    return { user: await assembleUser(data.user) };
  },

  /** Sign out of the current session. */
  async logout(): Promise<void> {
    await supabase.auth.signOut();
  },

  /**
   * Password-reset email · uniform response. Supabase resolves whether or not
   * the address exists, so this stays account-enumeration-safe.
   */
  async forgotPassword(body: ForgotBody): Promise<{ status: string }> {
    const { error } = await supabase.auth.resetPasswordForEmail(body.email, {
      redirectTo: `${appOrigin()}/reset-password`,
    });
    if (error) throw new Error(mapAuthError(error.message));
    return { status: 'pending_email' };
  },

  /**
   * Set a new password · the reset link established a recovery session via
   * detectSessionInUrl, so this is updateUser on the now-active session.
   */
  async resetPassword(body: ResetPasswordBody): Promise<{ user: User }> {
    const { data, error } = await supabase.auth.updateUser({ password: body.password });
    if (error || !data.user) throw new Error(mapAuthError(error?.message ?? ''));
    // Password is now changed. Enriching from `profiles` is a SECOND network call
    // whose failure must not report the saved password as an error ("Load failed"
    // on a success · prod 13 AUG). Degrade to a minimal user on that failure.
    try {
      return { user: await assembleUser(data.user) };
    } catch {
      return { user: minimalUser(data.user) };
    }
  },

  /** Start Google OAuth · supabase-js performs the browser redirect itself. */
  async googleStart(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${appOrigin()}/auth/callback` },
    });
    if (error) throw new Error(mapAuthError(error.message));
  },

  /**
   * Update the display name · SM.3b profile screen. Writes to Supabase Auth
   * user_metadata; the resulting USER_UPDATED event refreshes AuthContext.
   */
  async updateProfile(body: { displayName: string }): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      data: { display_name: body.displayName },
    });
    if (error) throw new Error(mapAuthError(error.message));
  },

  /**
   * Change the password while logged in · SM.3b profile screen. Distinct from
   * `resetPassword` (which runs on an email-recovery session) — same updateUser
   * call underneath, kept as a separate method for call-site clarity.
   */
  async updatePassword(body: { password: string }): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: body.password });
    if (error) throw new Error(mapAuthError(error.message));
  },
};

/** Map raw Supabase auth error text to user-facing RU copy. */
function mapAuthError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('not confirmed')) {
    return 'Подтверди email — ссылка в письме, которое мы прислали.';
  }
  if (m.includes('invalid login credentials') || m.includes('invalid credentials')) {
    return 'Неверный email или пароль.';
  }
  if (m.includes('already registered') || m.includes('already been registered')) {
    return 'Этот email уже зарегистрирован.';
  }
  if (m.includes('weak password') || m.includes('password should be')) {
    return 'Пароль слишком простой — минимум 8 символов.';
  }
  if (m.includes('same') && m.includes('password')) {
    return 'Новый пароль совпадает со старым — придумай другой.';
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Слишком много попыток · подожди минуту и попробуй снова.';
  }
  return raw || 'Что-то пошло не так · попробуй ещё раз.';
}
